/* eslint-disable @typescript-eslint/no-empty-function,@typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import gtm from '@analytics/google-tag-manager'
import segmentPlugin from '@analytics/segment'
import { getSessionStorageItem, setSessionStorageItem } from '@lib/tracking/sessionStorage'
import analytics from 'analytics'

import { isBrowser } from '../ssrMode'
import matomoPlugin from './Matomo'
import matomoTagManagerPlugin from './MatomoTagManager'

const LAST_REPORTED_PEGE_VIEW_URL = 'lastReportedPageViewURL'

declare global {
  interface Window {
    _paq: Array<(string | null | undefined)[]>
    dataLayer: any[]
  }
}

export interface Analytics {
  enableSegment: () => void
  trackCheckoutStarted: (opts: TrackCheckoutStartedOptions) => void
  trackDiscountCodeApplied: (opts: TrackOrderOptions) => void
  trackEvent: (opts: TrackEventOptions) => void
  trackGoal: (opts: TrackGoalOptions) => void
  trackIdentify: (opts: TrackIdentifyOptions) => void
  trackOrderCompleted: (opts: TrackOrderCompletedOptions) => void
  trackOrderConfirmationsChanged: (opts: TrackOrderOptions) => void
  trackOrderDetailsFilled: (opts: TrackOrderOptions) => void
  trackOrderPaymentMethodSelected: (opts: TrackOrderOptions) => void
  trackOrderPaymentStarted: (opts: TrackOrderOptions) => void
  trackOrderWalletPaymentStarted: (opts: TrackOrderOptions) => void
  trackPage: () => void
  trackTagManagerEvent: (opts: TrackEventOptions) => void
  user: () => User
}

export interface TrackEventOptions {
  action: string
  category: string
  label?: string | null
  location: string
}

export interface TrackGoalOptions {
  formAnalyticsGoal: string | null | undefined
}

export interface TrackIdentifyOptions {
  email: string
}

export interface TrackCheckoutStartedOptions {
  action: string
  category: string
  currency?: string | null
  discountCode?: string | null
  discountValue?: number | null
  email?: string | null
  eventSlug?: string | null
  orderNumber: string | null
  products?: {
    productId?: string | null
    productName?: string | null
    productPrice?: number | null
    productQuantity?: number
  }[]
  total?: number | null
}

export interface TrackOrderOptions {
  acceptedConfirmations?: string[]
  currency?: string | null
  discountCode?: string | null
  discountValue?: number | null
  email?: string | null
  eventSlug?: string | null
  orderNumber: string | null
  paymentMethod?: string
  products?: {
    productId?: string | null
    productName?: string | null
    productPrice?: number | null
    productQuantity?: number
  }[]
  total?: number | null
}

export interface TrackOrderCompletedOptions {
  acceptedConfirmations?: string[]
  currency?: string | null
  discountCode?: string | null
  discountValue?: number | null
  email?: string | null
  eventSlug?: string | null
  orderId?: string | null
  orderNumber?: string | null
  paymentMethod?: string
  products: {
    productId?: string | null
    productName?: string | null
    productPrice?: number | null
    productQuantity?: number
  }[]
  total?: number
}

export interface User {
  clientId?: string
}

export interface AnalyticsOptions {
  baseGoogleAnalyticsTrackingId?: string
  googleAnalyticsLinkerDomains?: string[]
  googleAnalyticsTrackingId?: string
  googleTagManagerId?: string
  matomoSiteId?: string
  matomoTagManagerContainerId?: string
  matomoUrl?: string
  segmentId?: string
}

export const create = ({
  googleAnalyticsTrackingId,
  googleTagManagerId,
  googleAnalyticsLinkerDomains = [],
  matomoSiteId,
  matomoTagManagerContainerId,
  matomoUrl,
  segmentId,
}: AnalyticsOptions = {}): Analytics => {
  const plugins = []
  const gaEnabled = isBrowser && googleAnalyticsTrackingId
  const gtmEnabled = isBrowser && googleTagManagerId
  const matomoEnabled = matomoSiteId && matomoUrl
  const matomoTagManagerEnabled = matomoUrl && matomoTagManagerContainerId
  const webHost = process.env.WEB_HOST || 'websummit.com'

  if (gtmEnabled) {
    plugins.push(gtm({ containerId: googleTagManagerId }))
  }

  if (isBrowser && matomoEnabled) {
    plugins.push(
      matomoPlugin({
        id: matomoSiteId,
        url: matomoUrl,
      })
    )
  }

  if (isBrowser && matomoTagManagerEnabled) {
    plugins.push(
      matomoTagManagerPlugin({
        container: matomoTagManagerContainerId,
        url: matomoUrl,
      })
    )
  }

  if (isBrowser && segmentId) {
    plugins.push(
      segmentPlugin({
        customScriptSrc: `https://segment-cdn.${webHost}/analytics.js/v1/${segmentId}/analytics.min.js`,
        enabled: false,
        writeKey: segmentId,
      })
    )
  }

  const tracker = analytics({ plugins })

  if (gaEnabled) {
    tracker.ready(async () => {})
  }

  if (gaEnabled && googleAnalyticsLinkerDomains) {
    tracker.ready(() => {})
  }

  const enableSegment = async () => {
    if (segmentId) {
      await tracker.plugins.enable('segment' as string & any[])
      await tracker.page()
    }
  }

  const trackTagManager = {
    plugins: {
      matomo: false, // <-- Disable matomo but call all others
      matomoTagManager: true,
    },
  }
  const trackMatomo = {
    plugins: {
      matomo: true, // <-- Enable tracking calls for matomo
      matomoTagManager: false,
    },
  }

  // Set lastReportedPageViewUrl to referrer first time session is created,
  // lastReportedPageViewUrl is used as referrer when reporting new page view
  if (!getSessionStorageItem(LAST_REPORTED_PEGE_VIEW_URL)) {
    setSessionStorageItem(
      LAST_REPORTED_PEGE_VIEW_URL,
      (typeof document !== 'undefined' && document.referrer) || ''
    )
  }

  return {
    enableSegment: async () => enableSegment(),
    trackCheckoutStarted: async ({
      action,
      category,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
    }: TrackCheckoutStartedOptions) => {
      await enableSegment()
      await tracker.track(
        'Checkout Started',
        {
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          products,
          value: total,
        },
        trackTagManager
      )
      products?.forEach(product => {
        const { productId, productName, productPrice, productQuantity } = product
        window._paq.push([
          'addEcommerceItem',
          productId,
          productName,
          'tickets',
          productPrice as string | null | undefined,
          productQuantity as string | null | undefined,
        ])
      })
      await tracker.track('trackEvent', { action, category, label: orderNumber }, trackMatomo)
    },
    trackDiscountCodeApplied: async ({
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Discount Code Applied',
        {
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackEvent: async ({ action, category, label, location }: TrackEventOptions) => {
      await tracker.track(action, { category, label, location }, trackTagManager)
      await tracker.track('trackEvent', { action, category, label }, trackMatomo)
    },
    trackGoal: ({ formAnalyticsGoal }: TrackGoalOptions) => {
      window._paq.push(['trackGoal', formAnalyticsGoal])
    },
    trackIdentify: async ({ email }: TrackIdentifyOptions) => {
      window.dataLayer?.push({ email })
      await tracker.identify(email)
    },
    trackOrderCompleted: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      paymentMethod,
      products,
    }: TrackOrderCompletedOptions) => {
      await enableSegment()
      await tracker.track(
        'Order_Completed',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
      window._paq.push(['trackEcommerceOrder', orderNumber, total as string | undefined])
    },
    trackOrderConfirmationsChanged: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
      paymentMethod,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Order Confirmations Changed',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackOrderDetailsFilled: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
      paymentMethod,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Order Details Filled',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackOrderPaymentMethodSelected: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
      paymentMethod,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Order Payment Method Selected',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackOrderPaymentStarted: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
      paymentMethod,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Order Payment Started',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackOrderWalletPaymentStarted: async ({
      acceptedConfirmations,
      orderNumber,
      total,
      discountValue,
      discountCode,
      currency,
      email,
      eventSlug,
      products,
      paymentMethod,
    }: TrackOrderOptions) => {
      await enableSegment()
      await tracker.track(
        'Order Wallet Payment Started',
        {
          acceptedConfirmations,
          brand: eventSlug,
          coupon: discountCode,
          currency,
          discount: discountValue,
          email,
          order_id: orderNumber,
          paymentMethod,
          products,
          value: total,
        },
        trackTagManager
      )
    },
    trackPage: async () => {
      const url = typeof document !== 'undefined' && document.URL && new URL(document.URL)
      const lastReportedPageViewUrl = getSessionStorageItem(LAST_REPORTED_PEGE_VIEW_URL)
      const referrer = lastReportedPageViewUrl && new URL(lastReportedPageViewUrl)

      if (url && referrer && referrer.pathname === url.pathname) {
        return
      }

      await tracker.page({ referrer: referrer?.toString() })

      if (url) {
        setSessionStorageItem(LAST_REPORTED_PEGE_VIEW_URL, url.toString())
      }
    },
    trackTagManagerEvent: async ({ action, category, label, location }: TrackEventOptions) => {
      await tracker.track(action, { category, label, location }, trackTagManager)
    },
    user: () => ({ clientId: tracker.user('traits.clientId') }),
  }
}
